/**
 * Domain Process Library
 * 
 * Pre-configured complex workflow definitions for various industries
 * Can be used with ComplexWorkflowVisualizer
 */

export const DOMAIN_PROCESSES = {
    // Healthcare Processes
    HEALTHCARE_RCM: {
        id: 'healthcare-rcm',
        name: 'Revenue Cycle Management',
        domain: 'Healthcare',
        description: 'Complete patient financial journey from registration to payment',
        complexity: 'extreme',
        avgDuration: '60-120 days',
        stages: [
            {
                id: 'patient-acquisition',
                name: 'Patient Acquisition',
                type: 'entry',
                actors: ['Patient', 'Front Desk', 'System'],
                steps: [
                    { id: 'registration', name: 'Patient Registration', actor: 'Front Desk' },
                    { id: 'insurance-verify', name: 'Insurance Verification', actor: 'System' },
                    { id: 'eligibility-check', name: 'Eligibility Check', actor: 'System' }
                ]
            },
            {
                id: 'pre-authorization',
                name: 'Pre-Authorization',
                type: 'process',
                actors: ['Clinical Staff', 'Insurance', 'System'],
                steps: [
                    { id: 'medical-necessity', name: 'Medical Necessity Review', actor: 'Clinical Staff' },
                    { id: 'auth-request', name: 'Submit Auth Request', actor: 'System' },
                    { id: 'auth-response', name: 'Receive Authorization', actor: 'Insurance' }
                ]
            },
            {
                id: 'service-delivery',
                name: 'Service Delivery',
                type: 'process',
                actors: ['Provider', 'Nurse', 'Patient'],
                steps: [
                    { id: 'schedule', name: 'Schedule Service', actor: 'Front Desk' },
                    { id: 'staff-verify', name: 'Verify Staff Credentials', actor: 'System' },
                    { id: 'render-service', name: 'Render Service', actor: 'Provider' },
                    { id: 'document', name: 'Document Care', actor: 'Provider' }
                ],
                loops: true
            },
            {
                id: 'claims-submission',
                name: 'Claims & Billing',
                type: 'process',
                actors: ['Billing', 'System', 'Payer'],
                steps: [
                    { id: 'coding', name: 'Medical Coding', actor: 'Billing' },
                    { id: 'claim-generation', name: 'Generate 837 Claim', actor: 'System' },
                    { id: 'x12-submission', name: 'Submit X12 File', actor: 'System', external: true },
                    { id: 'claim-status', name: 'Check Claim Status', actor: 'System' }
                ]
            },
            {
                id: 'payment-posting',
                name: 'Payment & Remittance',
                type: 'process',
                actors: ['Payer', 'Billing', 'System'],
                steps: [
                    { id: 'era-835', name: 'Receive 835 ERA', actor: 'System', external: true },
                    { id: 'payment-post', name: 'Post Payment', actor: 'Billing' },
                    { id: 'denial-management', name: 'Handle Denials', actor: 'Billing', conditional: true }
                ],
                loops: true
            }
        ],
        connections: [
            { from: 'patient-acquisition', to: 'pre-authorization' },
            { from: 'pre-authorization', to: 'service-delivery' },
            { from: 'service-delivery', to: 'claims-submission' },
            { from: 'claims-submission', to: 'payment-posting' },
            { from: 'payment-posting', to: 'claims-submission', type: 'loop', condition: 'denial' },
            { from: 'service-delivery', to: 'service-delivery', type: 'loop', condition: 'ongoing' }
        ]
    },

    HEALTHCARE_PRIOR_AUTH: {
        id: 'healthcare-prior-auth',
        name: 'Prior Authorization Workflow',
        domain: 'Healthcare',
        description: 'Insurance authorization for medical procedures',
        complexity: 'high',
        avgDuration: '5-15 days',
        stages: [
            {
                id: 'request-init',
                name: 'Request Initiation',
                type: 'entry',
                actors: ['Provider', 'Clinical Staff'],
                steps: [
                    { id: 'identify-need', name: 'Identify Auth Need', actor: 'Provider' },
                    { id: 'gather-clinical', name: 'Gather Clinical Info', actor: 'Clinical Staff' },
                    { id: 'check-guidelines', name: 'Check Payer Guidelines', actor: 'System' }
                ]
            },
            {
                id: 'submission',
                name: 'Auth Submission',
                type: 'process',
                actors: ['Auth Specialist', 'System'],
                steps: [
                    { id: 'complete-forms', name: 'Complete Auth Forms', actor: 'Auth Specialist' },
                    { id: 'attach-documents', name: 'Attach Supporting Docs', actor: 'Auth Specialist' },
                    { id: 'submit-portal', name: 'Submit via Portal/X12', actor: 'System', external: true }
                ]
            },
            {
                id: 'review-process',
                name: 'Payer Review',
                type: 'process',
                actors: ['Payer', 'Medical Director'],
                steps: [
                    { id: 'initial-review', name: 'Initial Review', actor: 'Payer' },
                    { id: 'medical-review', name: 'Medical Review', actor: 'Medical Director', conditional: true },
                    { id: 'decision', name: 'Authorization Decision', actor: 'Payer' }
                ],
                loops: true
            },
            {
                id: 'appeals',
                name: 'Appeals Process',
                type: 'process',
                actors: ['Provider', 'Payer', 'External Reviewer'],
                steps: [
                    { id: 'peer-to-peer', name: 'Peer-to-Peer Review', actor: 'Provider', conditional: true },
                    { id: 'formal-appeal', name: 'Formal Appeal', actor: 'Provider', conditional: true },
                    { id: 'external-review', name: 'External Review', actor: 'External Reviewer', conditional: true }
                ],
                loops: true
            }
        ],
        connections: [
            { from: 'request-init', to: 'submission' },
            { from: 'submission', to: 'review-process' },
            { from: 'review-process', to: 'appeals', condition: 'denied' },
            { from: 'appeals', to: 'review-process', type: 'loop', condition: 'retry' }
        ]
    },

    // Financial Services Processes
    MORTGAGE_ORIGINATION: {
        id: 'mortgage-origination',
        name: 'Mortgage Origination Process',
        domain: 'Financial Services',
        description: 'Complete mortgage application to closing process',
        complexity: 'high',
        avgDuration: '30-45 days',
        stages: [
            {
                id: 'application',
                name: 'Application',
                type: 'entry',
                actors: ['Borrower', 'Loan Officer', 'System'],
                steps: [
                    { id: 'pre-qual', name: 'Pre-qualification', actor: 'Loan Officer' },
                    { id: 'application-1003', name: 'Complete 1003 Application', actor: 'Borrower' },
                    { id: 'credit-pull', name: 'Credit Report Pull', actor: 'System', external: true },
                    { id: 'initial-disclosures', name: 'Send Initial Disclosures', actor: 'System' }
                ]
            },
            {
                id: 'processing',
                name: 'Processing',
                type: 'process',
                actors: ['Processor', 'Third Parties'],
                steps: [
                    { id: 'order-appraisal', name: 'Order Appraisal', actor: 'Processor' },
                    { id: 'order-title', name: 'Order Title Search', actor: 'Processor', external: true },
                    { id: 'voe-voi', name: 'Verify Employment/Income', actor: 'Processor', external: true },
                    { id: 'collect-docs', name: 'Collect Documents', actor: 'Processor' }
                ],
                loops: true
            },
            {
                id: 'underwriting',
                name: 'Underwriting',
                type: 'process',
                actors: ['Underwriter', 'System'],
                steps: [
                    { id: 'aus-submission', name: 'AUS Submission', actor: 'System', external: true },
                    { id: 'uw-review', name: 'Underwriter Review', actor: 'Underwriter' },
                    { id: 'conditions', name: 'Issue Conditions', actor: 'Underwriter', conditional: true },
                    { id: 'clear-conditions', name: 'Clear Conditions', actor: 'Processor', conditional: true }
                ],
                loops: true
            },
            {
                id: 'closing',
                name: 'Closing',
                type: 'process',
                actors: ['Closer', 'Title Company', 'Borrower'],
                steps: [
                    { id: 'clear-to-close', name: 'Clear to Close', actor: 'Underwriter' },
                    { id: 'closing-disclosure', name: 'Issue Closing Disclosure', actor: 'Closer' },
                    { id: 'schedule-closing', name: 'Schedule Closing', actor: 'Title Company', external: true },
                    { id: 'sign-documents', name: 'Sign Documents', actor: 'Borrower' },
                    { id: 'fund-loan', name: 'Fund Loan', actor: 'System' }
                ]
            }
        ],
        connections: [
            { from: 'application', to: 'processing' },
            { from: 'processing', to: 'underwriting' },
            { from: 'underwriting', to: 'processing', type: 'loop', condition: 'more-docs' },
            { from: 'underwriting', to: 'closing', condition: 'approved' }
        ]
    },

    // Supply Chain Processes
    SUPPLY_CHAIN_FULFILLMENT: {
        id: 'supply-chain-fulfillment',
        name: 'Order to Delivery',
        domain: 'Supply Chain',
        description: 'End-to-end order fulfillment process',
        complexity: 'high',
        avgDuration: '3-14 days',
        stages: [
            {
                id: 'order-management',
                name: 'Order Management',
                type: 'entry',
                actors: ['Customer', 'Sales', 'System'],
                steps: [
                    { id: 'order-entry', name: 'Order Entry', actor: 'Customer' },
                    { id: 'order-validation', name: 'Validate Order', actor: 'System' },
                    { id: 'credit-check', name: 'Credit Check', actor: 'System', external: true },
                    { id: 'inventory-allocation', name: 'Allocate Inventory', actor: 'System' }
                ]
            },
            {
                id: 'warehouse-ops',
                name: 'Warehouse Operations',
                type: 'process',
                actors: ['Warehouse', 'WMS', 'Picker'],
                steps: [
                    { id: 'pick-list', name: 'Generate Pick List', actor: 'WMS' },
                    { id: 'picking', name: 'Pick Items', actor: 'Picker' },
                    { id: 'packing', name: 'Pack Order', actor: 'Warehouse' },
                    { id: 'quality-check', name: 'Quality Check', actor: 'Warehouse', conditional: true }
                ],
                loops: true
            },
            {
                id: 'shipping',
                name: 'Shipping',
                type: 'process',
                actors: ['Shipping Dept', 'Carrier', 'System'],
                steps: [
                    { id: 'create-shipment', name: 'Create Shipment', actor: 'Shipping Dept' },
                    { id: 'print-labels', name: 'Print Labels', actor: 'System' },
                    { id: 'carrier-pickup', name: 'Carrier Pickup', actor: 'Carrier', external: true },
                    { id: 'tracking', name: 'Update Tracking', actor: 'System', external: true }
                ]
            },
            {
                id: 'delivery',
                name: 'Last Mile Delivery',
                type: 'process',
                actors: ['Carrier', 'Customer', 'System'],
                steps: [
                    { id: 'out-for-delivery', name: 'Out for Delivery', actor: 'Carrier' },
                    { id: 'delivery-attempt', name: 'Delivery Attempt', actor: 'Carrier', loops: true },
                    { id: 'proof-of-delivery', name: 'Capture POD', actor: 'Carrier' },
                    { id: 'customer-notification', name: 'Notify Customer', actor: 'System' }
                ]
            }
        ],
        connections: [
            { from: 'order-management', to: 'warehouse-ops' },
            { from: 'warehouse-ops', to: 'shipping' },
            { from: 'shipping', to: 'delivery' },
            { from: 'delivery', to: 'delivery', type: 'loop', condition: 'failed-attempt' }
        ]
    },

    // Manufacturing Processes
    MANUFACTURING_PRODUCTION: {
        id: 'manufacturing-production',
        name: 'Production Planning & Execution',
        domain: 'Manufacturing',
        description: 'Manufacturing production from planning to quality control',
        complexity: 'high',
        avgDuration: 'varies',
        stages: [
            {
                id: 'planning',
                name: 'Production Planning',
                type: 'entry',
                actors: ['Planner', 'System', 'Sales'],
                steps: [
                    { id: 'demand-forecast', name: 'Demand Forecasting', actor: 'System' },
                    { id: 'capacity-planning', name: 'Capacity Planning', actor: 'Planner' },
                    { id: 'material-planning', name: 'Material Requirements', actor: 'System' },
                    { id: 'schedule-production', name: 'Schedule Production', actor: 'Planner' }
                ]
            },
            {
                id: 'procurement',
                name: 'Procurement',
                type: 'process',
                actors: ['Purchasing', 'Supplier', 'QC'],
                steps: [
                    { id: 'create-po', name: 'Create Purchase Orders', actor: 'Purchasing' },
                    { id: 'supplier-confirm', name: 'Supplier Confirmation', actor: 'Supplier', external: true },
                    { id: 'receive-materials', name: 'Receive Materials', actor: 'Warehouse' },
                    { id: 'incoming-qc', name: 'Incoming QC', actor: 'QC', conditional: true }
                ]
            },
            {
                id: 'production',
                name: 'Production Execution',
                type: 'process',
                actors: ['Operator', 'Supervisor', 'Maintenance'],
                steps: [
                    { id: 'setup', name: 'Machine Setup', actor: 'Operator' },
                    { id: 'production-run', name: 'Production Run', actor: 'Operator' },
                    { id: 'in-process-qc', name: 'In-Process QC', actor: 'QC' },
                    { id: 'maintenance', name: 'Preventive Maintenance', actor: 'Maintenance', conditional: true }
                ],
                loops: true
            },
            {
                id: 'quality-control',
                name: 'Quality Control',
                type: 'process',
                actors: ['QC', 'Lab', 'Production'],
                steps: [
                    { id: 'final-inspection', name: 'Final Inspection', actor: 'QC' },
                    { id: 'testing', name: 'Product Testing', actor: 'Lab', conditional: true },
                    { id: 'rework', name: 'Rework if Needed', actor: 'Production', conditional: true },
                    { id: 'quality-release', name: 'Quality Release', actor: 'QC' }
                ],
                loops: true
            },
            {
                id: 'packaging-shipping',
                name: 'Packaging & Shipping',
                type: 'process',
                actors: ['Packaging', 'Warehouse', 'Logistics'],
                steps: [
                    { id: 'packaging', name: 'Package Products', actor: 'Packaging' },
                    { id: 'labeling', name: 'Apply Labels', actor: 'Packaging' },
                    { id: 'final-qc', name: 'Final QC Check', actor: 'QC' },
                    { id: 'ship-to-warehouse', name: 'Ship to Warehouse', actor: 'Logistics' }
                ]
            }
        ],
        connections: [
            { from: 'planning', to: 'procurement' },
            { from: 'procurement', to: 'production' },
            { from: 'production', to: 'quality-control' },
            { from: 'quality-control', to: 'production', type: 'loop', condition: 'failed-qc' },
            { from: 'quality-control', to: 'packaging-shipping', condition: 'passed' }
        ]
    },

    // Insurance Processes
    INSURANCE_CLAIMS: {
        id: 'insurance-claims',
        name: 'Insurance Claims Processing',
        domain: 'Insurance',
        description: 'Auto insurance claim from FNOL to settlement',
        complexity: 'medium',
        avgDuration: '7-30 days',
        stages: [
            {
                id: 'fnol',
                name: 'First Notice of Loss',
                type: 'entry',
                actors: ['Claimant', 'Agent', 'System'],
                steps: [
                    { id: 'report-claim', name: 'Report Claim', actor: 'Claimant' },
                    { id: 'collect-info', name: 'Collect Information', actor: 'Agent' },
                    { id: 'create-claim', name: 'Create Claim Record', actor: 'System' },
                    { id: 'assign-adjuster', name: 'Assign Adjuster', actor: 'System' }
                ]
            },
            {
                id: 'investigation',
                name: 'Investigation',
                type: 'process',
                actors: ['Adjuster', 'Third Party', 'Police'],
                steps: [
                    { id: 'contact-parties', name: 'Contact Involved Parties', actor: 'Adjuster' },
                    { id: 'police-report', name: 'Obtain Police Report', actor: 'Adjuster', external: true },
                    { id: 'damage-assessment', name: 'Assess Damage', actor: 'Adjuster' },
                    { id: 'liability-determination', name: 'Determine Liability', actor: 'Adjuster' }
                ]
            },
            {
                id: 'evaluation',
                name: 'Claim Evaluation',
                type: 'process',
                actors: ['Adjuster', 'Medical', 'Legal'],
                steps: [
                    { id: 'coverage-review', name: 'Review Coverage', actor: 'Adjuster' },
                    { id: 'medical-review', name: 'Medical Review', actor: 'Medical', conditional: true },
                    { id: 'estimate-damages', name: 'Estimate Damages', actor: 'Adjuster' },
                    { id: 'settlement-calc', name: 'Calculate Settlement', actor: 'System' }
                ]
            },
            {
                id: 'settlement',
                name: 'Settlement',
                type: 'process',
                actors: ['Adjuster', 'Claimant', 'Finance'],
                steps: [
                    { id: 'settlement-offer', name: 'Make Settlement Offer', actor: 'Adjuster' },
                    { id: 'negotiation', name: 'Negotiate', actor: 'Adjuster', conditional: true, loops: true },
                    { id: 'agreement', name: 'Reach Agreement', actor: 'Claimant' },
                    { id: 'payment', name: 'Process Payment', actor: 'Finance' }
                ]
            }
        ],
        connections: [
            { from: 'fnol', to: 'investigation' },
            { from: 'investigation', to: 'evaluation' },
            { from: 'evaluation', to: 'settlement' },
            { from: 'settlement', to: 'evaluation', type: 'loop', condition: 'dispute' }
        ]
    }
};

// Helper function to get process by ID
export const getProcessById = (processId: string) => {
    return Object.values(DOMAIN_PROCESSES).find(p => p.id === processId);
};

// Helper function to get all processes for a domain
export const getProcessesByDomain = (domain: string) => {
    return Object.values(DOMAIN_PROCESSES).filter(p => p.domain === domain);
};

// Get unique domains
export const getDomains = () => {
    return [...new Set(Object.values(DOMAIN_PROCESSES).map(p => p.domain))];
};

export default DOMAIN_PROCESSES;
